import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { contactsApi } from '../../api/contacts'
import { RefusedError } from '../../api/client'
import { formatDate, formatMoney } from '../../lib/format'
import { Avatar } from '../../components/Avatar'
import { Correct } from '../../components/Correct'
import { LineIcon } from '../../components/LineIcon'
import { VoteButtons } from '../../components/VoteButtons'

// Contact memory (PM-006) as a side panel: one person's history — the pursuits they sit in, the signals raised, and
// the last touch — beside the page that named them (the account plan). The Contacts page itself no longer opens one:
// a contact opened there goes to the card, a screen of its own (REQ-125, owner 2026-10-01).
export function ContactDrawer({ spoke, personId, onClose }: { spoke: string; personId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['contact', spoke, personId], queryFn: () => contactsApi.memory(spoke, personId) })
  const m = q.data
  const fileRef = useRef<HTMLInputElement>(null)
  const [stamp, setStamp] = useState(0)
  const upload = useMutation({
    mutationFn: (file: File) => contactsApi.uploadPhoto(spoke, personId, file),
    onSuccess: () => { setStamp(Date.now()); void qc.invalidateQueries({ queryKey: ['contact', spoke, personId] }); void qc.invalidateQueries({ queryKey: ['contacts', spoke] }); void qc.invalidateQueries({ queryKey: ['account', spoke] }) },
  })
  const photoUrl = m?.contact.person.photoUrl ? `${m.contact.person.photoUrl}${stamp ? `?v=${stamp}` : ''}` : null
  return (
    <aside className="deal-drawer contact-drawer">
      <div className="actions space-between">
        <div className="drawer-identity">
          {m && <Avatar name={m.contact.person.name} photoUrl={photoUrl} persona={m.contact.person.persona} role={m.contact.person.role} size={52} />}
          <div>
            <p className="kicker">{m?.contact.companyName ?? ''}</p>
            <h2>{m?.contact.person.name ?? 'Contact'}</h2>
          </div>
        </div>
        <div className="drawer-actions">
          {m && <VoteButtons spoke={spoke} personId={personId} vote={m.contact.vote} />}
          <Link className="button small" to={`/contacts/${encodeURIComponent(personId)}`} title="Everything on file about them, and your call on each fact">Open the card</Link>
          <button className="button small" onClick={onClose}>Close</button>
        </div>
      </div>
      {q.isPending && <p className="muted">Loading…</p>}
      {m && (
        <>
          <p className="photo-line small">
            <span className="muted">{m.contact.person.photoUrl ? `Photo from ${m.contact.person.photoSource ?? 'an enricher'}${m.contact.person.photoAt ? `, ${formatDate(m.contact.person.photoAt)}` : ''}.` : 'No photo on file.'}</span>
            <button className="link-button" onClick={() => fileRef.current?.click()} disabled={upload.isPending}><LineIcon name="upload" size={13} /> {upload.isPending ? 'Uploading…' : m.contact.person.photoUrl ? 'Replace photo' : 'Upload a photo'}</button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = '' }} />
          </p>
          {upload.isError && <p className="error small">{upload.error instanceof RefusedError ? upload.error.refusal.message : upload.error.message}</p>}
          <p className="drawer-correct">
            <Correct kind="person-company" entityId={personId} entityLabel={`${m.contact.person.name} · ${m.contact.companyName}`} currentValue={m.contact.person.companyId} compact />
            <Correct kind="contact-role" entityId={personId} entityLabel={m.contact.person.name} currentValue={m.contact.person.role} compact />
            <Correct kind="contact-persona" entityId={personId} entityLabel={m.contact.person.name} currentValue={m.contact.person.persona} compact />
            <span className="muted small">Wrong company, wrong relationship to the firm, or the wrong part played? Say so.</span>
          </p>
          <dl className="facts">
            {m.contact.person.title && <><dt>Title</dt><dd>{m.contact.person.title}</dd></>}
            {m.contact.person.email && <><dt>Email</dt><dd>{m.contact.person.email}</dd></>}
            {m.contact.person.location && <><dt>Writes from</dt><dd>{m.contact.person.location}</dd></>}
            <dt>Relationship</dt><dd>{Math.round((m.contact.person.relationshipStrength ?? 0) * 100)}% strength</dd>
            <dt>Last touch</dt><dd>{m.contact.lastActivity ? `${formatDate(m.contact.lastActivity)} (${m.contact.dormancyDays} days ago)` : '—'}</dd>
            {(m.contact.person.affiliations?.length ?? 0) > 0 && <><dt>Affiliations</dt><dd><ul className="chips">{m.contact.person.affiliations!.map((a) => <li key={a} className="chip on">{a}</li>)}</ul></dd></>}
          </dl>
          <h3>Pursuits</h3>
          {m.pursuits.length === 0 ? <p className="muted">No pursuits at this account.</p> : (
            <ul className="plain">
              {m.pursuits.map((e) => (
                <li key={e.engagementId}>
                  <strong>{e.subject}</strong>
                  <span className="muted small">{e.stage}{e.amount ? ` · ${formatMoney(e.amount)}` : ''}{e.lastActivityOn ? ` · ${formatDate(e.lastActivityOn)}` : ''}</span>
                </li>
              ))}
            </ul>
          )}
          <h3>History &amp; signals</h3>
          {m.signals.length === 0 ? <p className="muted">Nothing flagged.</p> : (
            <ul className="plain signals">
              {m.signals.slice(0, 15).map((s) => (
                <li key={s.signalId}><span className={`tag sig ${s.severity}`}>{s.type}</span> <span>{s.headline}</span><span className="muted small"> · {formatDate(s.occurredOn)}</span></li>
              ))}
            </ul>
          )}
        </>
      )}
    </aside>
  )
}
